import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Divider, Modal, Row, Checkbox } from 'antd';
import { Formik, FormikProps } from 'formik';
import { Form, Select } from 'formik-antd';
import * as Yup from 'yup';
import { has, isEmpty, isNil, isNumber } from 'lodash';
import bankersRounding from 'bankers-rounding';

import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import {
  MultiRateSchedulerEditModel,
  CustomerMspOrVmsOverviewDto,
  OvertimeEnum,
} from 'api/backofficeApiClient';
import FormItem from 'components/formik/FormItem';
import DatePicker from 'components/formik/DatePicker';
import SubmitButton from 'components/formik/SubmitButton';
import BaseIndicatorNumber from 'components/wrappers/BaseIndicatorNumber';
import InputNumber from 'components/formik/InputNumber';
import { getFormItemName } from 'utils/fieldArrayUtils';
import { FinancialInfoCalculator } from '../AssignmentDataModal/FinancialInfo/calculationUtils';
import { getDecimalSafeValue } from 'utils/mathUtils';
import {
  getFinancialWarningMessages,
  getOvertimeOptions,
} from 'utils/assignmentUtils';
import { SimplePriceAgreementW2Info } from '../AssignmentDataModal/FinancialInfo';

import styles from './FinancialInfoSchedulerModal.module.less';

export enum EffectiveDateMode {
  ADD = 1,
  EDIT = 2,
}
const multiRateSchedulersSchema = Yup.object({
  payRate: Yup.number()
    .nullable()
    .min(0, 'Pay rate should be from 0 to 999999')
    .max(999999, 'Pay rate should be from 0 to 999999'),
  billRate: Yup.number()
    .nullable()
    .min(0, 'Bill rate should be from 0 to 999999')
    .max(999999, 'Bill rate should be from 0 to 999999'),
});

const getFinancialInfoSchedulerSchema = (
  values: FinancialSchedulerFormValues,
) => {
  const schema: any = {};

  if (values?.financialInfoScheduler?.hasOwnProperty('payRate')) {
    schema.payRate = Yup.number()
      .nullable()
      .min(0, 'Pay rate should be from 0 to 999999')
      .max(999999, 'Pay rate should be from 0 to 999999')
      .when('billRate', {
        is: (billRate: number | null) => isNumber(billRate),
        then: Yup.number()
          .nullable()
          .max(Yup.ref('billRate'), 'Pay rate should be less than bill rate'),
      })
      .required('Field is required');
  }

  if (values?.financialInfoScheduler?.hasOwnProperty('billRate')) {
    schema.billRate = Yup.number()
      .nullable()
      .min(0, 'Bill rate should be from 0 to 999999')
      .max(999999, 'Bill rate should be from 0 to 999999')
      .required('Field is required');
  }

  if (values?.financialInfoScheduler?.hasOwnProperty('profitRate')) {
    schema.profitRate = Yup.number()
      .nullable()
      .min(0, 'Profit rate should be from 0 to 999999')
      .max(999999, 'Profit rate should be from 0 to 999999')
      .required('Field is required');
  }

  if (values?.financialInfoScheduler?.hasOwnProperty('dtBillRate')) {
    schema.dtBillRate = Yup.number()
      .nullable()
      .min(0, 'DT Bill rate should be from 0 to 999999')
      .max(999999, 'DT Bill rate should be from 0 to 999999')
      .required('Field is required');
  }

  if (values?.financialInfoScheduler?.hasOwnProperty('overtimeBillRate')) {
    schema.overtimeBillRate = Yup.number()
      .nullable()
      .min(0, 'OT bill rate should be from 0 to 999999')
      .max(999999, 'OT bill rate should be from 0 to 999999')
      .required('Field is required');
  }

  if (values?.financialInfoScheduler?.hasOwnProperty('overtimePayRate')) {
    schema.overtimePayRate = Yup.number()
      .nullable()
      .min(0, 'OT pay rate should be from 0 to 999999')
      .max(999999, 'OT pay rate should be from 0 to 999999')
      .required('Field is required');
  }

  return schema;
};

const getValidationSchema = (values: FinancialSchedulerFormValues) =>
  Yup.object({
    financialInfoScheduler: Yup.object(getFinancialInfoSchedulerSchema(values)),
    multiRateSchedulers: Yup.array().of(multiRateSchedulersSchema),
    effectiveDate: Yup.date().nullable().required('Field is required'),
  });

export interface FinancialInfoSchedulerModel {
  payRate?: number;
  billRate?: number;
  dtBillRate?: number;
  overtimeBillRate?: number;
  overtimePayRate?: number;
  profitRate?: number;
  overtime?: OvertimeEnum;
  mbpRate?: number;
}

export interface FinancialSchedulerFormValues {
  effectiveDate?: Date;
  financialInfoScheduler?: FinancialInfoSchedulerModel;
  multiRateSchedulers?: MultiRateSchedulerEditModel[];
}

type Props = {
  mode: EffectiveDateMode;
  onClose: () => void;
  initialValues?: FinancialSchedulerFormValues;
  onSuccess: (data: FinancialSchedulerFormValues) => Promise<void> | void;
  mspOrVmsOverviewInfo?: CustomerMspOrVmsOverviewDto;
  onRemoveFinancialScheduler?: () => void;
  externalRef?: any;
  w2InfoByWorkSiteWorkLocation?: SimplePriceAgreementW2Info;
  workStateLocation?: number;
  isW2Assignment: boolean;
};

export default function FinancialInfoSchedulerModal(props: Props) {
  const {
    onClose,
    initialValues,
    onSuccess,
    mode,
    mspOrVmsOverviewInfo,
    onRemoveFinancialScheduler,
    w2InfoByWorkSiteWorkLocation,
    workStateLocation,
    isW2Assignment,
  } = props;
  const isUpdateMode = mode === EffectiveDateMode.EDIT;
  const [isDisabledFields, setIsDisabledFields] = useState(isUpdateMode);
  const formRef: any = useRef();

  const { externalRef = formRef } = props;

  const handleIsDisabledFields = () => {
    setIsDisabledFields(!isDisabledFields);
  };

  const getInitialValues = (): FinancialSchedulerFormValues => {
    if (initialValues) return initialValues;
    return {
      financialInfoScheduler: undefined,
      multiRateSchedulers: [],
      effectiveDate: undefined,
    };
  };

  const handleRemoveSchedule = () => {
    return Modal.confirm({
      title: 'Scheduled financial rates update will be removed. Are you sure?',
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        onRemoveFinancialScheduler && (await onRemoveFinancialScheduler());
      },
      onCancel() {},
    });
  };

  const handleCloseModal = () => {
    if (externalRef.current.isSubmitting) return;

    if (externalRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const getCorrectTitle = () => {
    switch (mode) {
      case EffectiveDateMode.ADD:
        return 'Warning! Financial rates were updated which might affect your billing. What is the effective date for this change?';
      case EffectiveDateMode.EDIT:
        return 'The following fields are scheduled for changes:';
    }
  };

  const financialWarningMessages = getFinancialWarningMessages({
    mspVmsValues: mspOrVmsOverviewInfo,
    hasW2InfoByWorkSiteWorkLocation:
      !!w2InfoByWorkSiteWorkLocation ||
      isNil(workStateLocation) ||
      !isW2Assignment,
  });
  const financialWarningMessageExists = !!financialWarningMessages.length;

  const getSafeComputedValue = (value: number) => {
    return financialWarningMessageExists ? 0 : getDecimalSafeValue(value);
  };

  useEffect(() => {
    has(initialValues?.financialInfoScheduler, 'profitRate') &&
      calculateProfitRateValue(false);
    // eslint-disable-next-line
  }, []);

  const calculateProfitRateValue = (profitRateTouched: boolean = true) => {
    setTimeout(() => {
      if (!externalRef.current) return;

      const { values, setFieldValue, setFieldTouched } =
        externalRef.current as FormikProps<FinancialSchedulerFormValues>;

      const billRate = values.financialInfoScheduler?.billRate;
      const payRate = values.financialInfoScheduler?.payRate;

      // Reset profitRate if billRate ot payRate is not provided
      const shouldResetValue = isNil(billRate) || isNil(payRate);
      const profitRateValue = FinancialInfoCalculator.getProfitRateValue(
        values.financialInfoScheduler as FinancialInfoSchedulerModel,
        mspOrVmsOverviewInfo,
      );
      const safeValue = getSafeComputedValue(profitRateValue);

      setFieldValue(
        'financialInfoScheduler.profitRate',
        shouldResetValue ? 0 : safeValue,
      );
      setFieldTouched('financialInfoScheduler.profitRate', profitRateTouched);
    }, 0);
  };

  const calculateDTPayRate = () => {
    setTimeout(() => {
      const { values, setFieldValue, setFieldTouched } =
        externalRef.current as FormikProps<FinancialSchedulerFormValues>;

      if (values?.financialInfoScheduler?.overtime === OvertimeEnum.Exempt)
        return;

      const { financialInfoScheduler } = values;

      const value = bankersRounding(
        (financialInfoScheduler?.payRate || 0) * 2,
        2,
      );

      setFieldValue('financialInfoScheduler.dtPayRate', value);
      setFieldTouched('financialInfoScheduler.dtPayRate', true);
    }, 0);
  };

  const calculateOvertimePayRate = () => {
    setTimeout(() => {
      const { values, setFieldValue, setFieldTouched } =
        externalRef.current as FormikProps<FinancialSchedulerFormValues>;

      const { financialInfoScheduler } = values;

      const value = bankersRounding(
        (financialInfoScheduler?.payRate || 0) * 1.5,
        2,
      );

      setFieldValue('financialInfoScheduler.overtimePayRate', value);
      setFieldTouched('financialInfoScheduler.overtimePayRate', true);
    }, 0);
  };

  const handlePayRateChange = () => {
    if (initialValues?.financialInfoScheduler?.overtimePayRate) {
      calculateOvertimePayRate();
    }

    calculateProfitRateValue();
    calculateDTPayRate();
  };

  const handleSubmit = async (data: FinancialSchedulerFormValues) => {
    await onSuccess(data);
    onClose();
  };

  return (
    <BaseModal
      open
      title="Update financial information?"
      maxWidth={638}
      onCancel={handleCloseModal}
    >
      <Formik
        enableReinitialize
        innerRef={externalRef}
        validationSchema={getValidationSchema(getInitialValues())}
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <span className="modal-title">{getCorrectTitle()}</span>
              {isUpdateMode && (
                <div className={styles.checkboxWrap}>
                  <Checkbox onChange={handleIsDisabledFields}>
                    Update the scheduled changes
                  </Checkbox>
                </div>
              )}
              <Row gutter={10}>
                <Col xs={24} md={24}>
                  <FormItem label="Effective date *" name="effectiveDate">
                    <DatePicker
                      name="effectiveDate"
                      disabled={isDisabledFields}
                      placeholder="Select end date"
                      showToday={false}
                      format="MM/DD/YYYY"
                      className="d-flex"
                    />
                  </FormItem>
                </Col>
                {formik.values?.financialInfoScheduler?.hasOwnProperty(
                  'payRate',
                ) && (
                  <>
                    <Col xs={24} md={24}>
                      <FormItem
                        label="Pay rate *"
                        name="financialInfoScheduler.payRate"
                      >
                        <InputNumber
                          disabled={isDisabledFields}
                          name="financialInfoScheduler.payRate"
                          placeholder="0.00"
                          className="full-width"
                          precision={2}
                          inputPrefix={'$'}
                          onValueChange={handlePayRateChange}
                        />
                      </FormItem>
                    </Col>
                  </>
                )}
                {formik.values?.financialInfoScheduler?.hasOwnProperty(
                  'billRate',
                ) && (
                  <>
                    <Col xs={24} md={24}>
                      <FormItem
                        label="Bill rate *"
                        name="financialInfoScheduler.billRate"
                      >
                        <InputNumber
                          disabled={isDisabledFields}
                          name="financialInfoScheduler.billRate"
                          placeholder="0.00"
                          className="full-width"
                          precision={2}
                          inputPrefix={'$'}
                          onValueChange={() => calculateProfitRateValue()}
                        />
                      </FormItem>
                    </Col>
                  </>
                )}
                {(formik.values?.financialInfoScheduler?.hasOwnProperty(
                  'billRate',
                ) ||
                  formik.values?.financialInfoScheduler?.hasOwnProperty(
                    'payRate',
                  )) && (
                  <>
                    <Col xs={24} md={24}>
                      <FormItem
                        label="Profit rate"
                        name="financialInfoScheduler.profitRate"
                      >
                        <InputNumber
                          disabled
                          allowNegative
                          name="financialInfoScheduler.profitRate"
                          placeholder="0.00"
                          className="full-width"
                          precision={2}
                        />
                      </FormItem>
                    </Col>
                    <Col xs={24} md={8} />
                  </>
                )}
                {formik.values?.financialInfoScheduler?.hasOwnProperty(
                  'overtime',
                ) && (
                  <>
                    <Col xs={24} md={24}>
                      <FormItem
                        name="financialInfoScheduler.overtime"
                        label="Overtime *"
                      >
                        <Select
                          name="financialInfoScheduler.overtime"
                          placeholder="Select overtime type"
                          className="full-width"
                          disabled
                        >
                          {getOvertimeOptions().map((overtime) => (
                            <Select.Option
                              key={`select-overtime-${overtime.value}`}
                              value={overtime.value || ''}
                            >
                              {overtime.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </FormItem>
                    </Col>
                  </>
                )}
                {formik.values?.financialInfoScheduler?.hasOwnProperty(
                  'overtimeBillRate',
                ) && (
                  <>
                    <Col xs={24} md={24}>
                      <FormItem
                        label="OT bill rate *"
                        name="financialInfoScheduler.overtimeBillRate"
                      >
                        <InputNumber
                          disabled={
                            isDisabledFields ||
                            formik.values?.financialInfoScheduler.overtime ===
                              OvertimeEnum.Exempt
                          }
                          name="financialInfoScheduler.overtimeBillRate"
                          placeholder="0.00"
                          className="full-width"
                          precision={2}
                          inputPrefix={'$'}
                        />
                      </FormItem>
                    </Col>
                  </>
                )}
                {formik.values?.financialInfoScheduler?.hasOwnProperty(
                  'overtimePayRate',
                ) && (
                  <>
                    <Col xs={24} md={24}>
                      <FormItem
                        label="OT pay rate *"
                        name="financialInfoScheduler.overtimePayRate"
                      >
                        <InputNumber
                          disabled={isDisabledFields}
                          name="financialInfoScheduler.overtimePayRate"
                          placeholder="0.00"
                          className="full-width"
                          precision={2}
                          inputPrefix={'$'}
                        />
                      </FormItem>
                    </Col>
                  </>
                )}
                {formik.values?.financialInfoScheduler?.hasOwnProperty(
                  'dtBillRate',
                ) && (
                  <>
                    <Col xs={24} md={24}>
                      <FormItem
                        label="DT Bill rate *"
                        name="financialInfoScheduler.dtBillRate"
                      >
                        <InputNumber
                          disabled={
                            isDisabledFields ||
                            formik.values?.financialInfoScheduler.overtime ===
                              OvertimeEnum.Exempt
                          }
                          placeholder="0.00"
                          name="financialInfoScheduler.dtBillRate"
                          className="full-width"
                          precision={2}
                          inputPrefix={'$'}
                        />
                      </FormItem>
                    </Col>
                  </>
                )}
                {!isNil(formik.values.financialInfoScheduler) &&
                  !isEmpty(formik.values.financialInfoScheduler) &&
                  (formik.values?.multiRateSchedulers || []).length > 0 && (
                    <Divider />
                  )}
                {(formik.values?.multiRateSchedulers || []).length > 0 &&
                  (formik.values.multiRateSchedulers || []).map(
                    (multiRates, index) => (
                      <React.Fragment key={index}>
                        {multiRates.hasOwnProperty('payRate') && (
                          <>
                            <Col xs={24} md={24}>
                              <FormItem
                                name={getFormItemName({
                                  name: 'payRate',
                                  index,
                                  prefix: 'multiRateSchedulers',
                                })}
                                label={BaseIndicatorNumber({
                                  number: (multiRates?.index || 0) + 2,
                                  title: 'Pay rate *',
                                })}
                              >
                                <InputNumber
                                  disabled={isDisabledFields}
                                  name={getFormItemName({
                                    name: 'payRate',
                                    index,
                                    prefix: 'multiRateSchedulers',
                                  })}
                                  placeholder="0.00"
                                  className="full-width"
                                  precision={2}
                                  inputPrefix={'$'}
                                />
                              </FormItem>
                            </Col>
                          </>
                        )}
                        {multiRates.hasOwnProperty('billRate') && (
                          <>
                            <Col xs={24} md={24}>
                              <FormItem
                                name={getFormItemName({
                                  name: 'billRate',
                                  index,
                                  prefix: 'multiRateSchedulers',
                                })}
                                label={BaseIndicatorNumber({
                                  number: (multiRates?.index || 0) + 2,
                                  title: 'Bill rate *',
                                })}
                              >
                                <InputNumber
                                  disabled={isDisabledFields}
                                  name={getFormItemName({
                                    name: 'billRate',
                                    index,
                                    prefix: 'multiRateSchedulers',
                                  })}
                                  placeholder="0.00"
                                  className="full-width"
                                  precision={2}
                                  inputPrefix={'$'}
                                />
                              </FormItem>
                            </Col>
                          </>
                        )}
                      </React.Fragment>
                    ),
                  )}
              </Row>
            </div>
            <BaseModalFooter>
              <Button onClick={handleCloseModal}>Cancel</Button>
              {isUpdateMode && (
                <Button type="primary" onClick={handleRemoveSchedule}>
                  Remove update
                </Button>
              )}
              <SubmitButton type="primary">Update</SubmitButton>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
